// src/pages/OrdineDettaglio.jsx
import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { Shirt, ArrowLeft, Store, CalendarDays, Truck, MapPin } from "lucide-react";
import toast from "react-hot-toast";
import { getOrderDetail } from "../../api/ordersApi";
import AddToCalendarButtons from "../../components/orders/AddToCalendarButtons";
import { useCustomerPresentation } from "../../utils/appMode";

// stesso CSS della pagina elenco (solo per la var --col-orderid)
import "../css/Ordini.css";

/* ---------- utils (copiati dalla lista per coerenza) ---------- */
function formatCurrency(v) {
  const n = Number(v);
  const isCents = Number.isInteger(n) && n >= 50;
  const eur = isCents ? n / 100 : n;
  return eur.toLocaleString("it-IT", { style: "currency", currency: "EUR" });
}
function formatDate(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  return d.toLocaleDateString("it-IT");
}
const isCrossSell = (item) => Number(item?.id_servizio) === 0;

function viewerRole() {
  try {
    const payload = localStorage.getItem("token")?.split(".")[1];
    const user = JSON.parse(atob(payload.replace(/-/g, "+").replace(/_/g, "/")));
    return String(user.role || user.user?.role || "").toLowerCase();
  } catch { return false; }
}
function articleImage(item) {
  const url = item?.immagine || item?.image_url;
  return typeof url === "string" && /^https?:\/\//i.test(url) ? url : null;
}
function OrderThumbnail({ url }) {
  return <span className="customer-order-thumb" aria-hidden="true">
    <Shirt size={26} />
    {url && <img key={url} src={url} alt="" loading="lazy" onError={event => { event.currentTarget.style.visibility = "hidden"; }} />}
  </span>;
}

export default function OrdineDettaglio() {
  const isCustomer = useCustomerPresentation(viewerRole());
  const { id } = useParams();
  const [loading, setLoading] = useState(true);
  const [order, setOrder] = useState(null);
  const [error, setError] = useState("");
  const navigate = useNavigate();
  const location = useLocation();

  const backTo = location.state?.from; // <- viene da Profilo (o da altre pagine)

  useEffect(() => {
    let alive = true;
    setLoading(true);
    setError("");

    getOrderDetail(id)
      .then((data) => {
        if (!alive) return;
        setOrder(data);
      })
      .catch((e) => {
        if (!alive) return;
        const msg = e?.message || "Errore nel caricamento ordine";
        setError(msg);
        toast.error("Impossibile caricare l'ordine");
      })
      .finally(() => alive && setLoading(false));

    return () => {
      alive = false;
    };
  }, [id]);

  const items = useMemo(() => order?.items || [], [order]);
  const hasRows = items.length > 0;

  if (isCustomer) return (
    <div className="list-page list-page--detail customer-orders customer-order-detail">
      <header className="list-header">
        <button type="button" className="customer-order-back" onClick={() => {
          if (backTo) navigate(backTo);
          else navigate(-1);
        }}><ArrowLeft size={18} aria-hidden="true" />Torna agli ordini</button>
        <div className="customer-order-detail-heading">
          <div><h1>Ordine #{id}</h1><p className="customer-orders-description">{order?.created_at ? `Effettuato il ${formatDate(order.created_at)}` : loading ? "Caricamento ordine…" : ""}</p></div>
          <p className="customer-order-mobile-meta">Data ord. {order?.created_at ? formatDate(order.created_at) : loading ? "…" : "-"} · {order?.fornitore ?? (loading ? "…" : "-")}</p>
          {order && <span className="customer-order-status" data-status={order.raw_status}>{order.status || "-"}</span>}
        </div>
      </header>
      <main className="list-main">
        {error && !loading && <div className="list-empty" role="alert">{error}</div>}
        <section className="customer-order-panel customer-order-facts" aria-label="Riepilogo ordine">
          <div><Store size={24} aria-hidden="true" /><div><span>Fornitore</span><strong>{order?.fornitore ?? (loading ? "…" : "-")}</strong></div></div>
          <div><CalendarDays size={24} aria-hidden="true" /><div><span>Data ordine</span><strong>{order?.created_at ? formatDate(order.created_at) : loading ? "…" : "-"}</strong></div></div>
        </section>
        <AddToCalendarButtons order={order} actor="customer" disabled={loading} />
        <section className="customer-order-panel" aria-labelledby="customer-order-items-title">
          <h2 id="customer-order-items-title">Articoli ordinati</h2>
          <div className="customer-order-items" role="table" aria-label="Articoli ordinati" aria-busy={loading}>
            <div className="customer-order-item customer-order-item--head" role="row"><span role="columnheader">Articolo</span><span role="columnheader">Qta</span><span role="columnheader">Prezzo</span><span role="columnheader">Totale</span></div>
            {loading && <p className="customer-order-skeleton">Caricamento articoli…</p>}
            {!loading && !hasRows && <p className="list-empty">Nessun articolo.</p>}
            {!loading && items.map(r => <div className="customer-order-item" role="row" key={r.id}>
              <div className="customer-order-item-name" role="cell"><OrderThumbnail url={articleImage(r)} /><div><strong>{r.descrizione}</strong>{r.tipo && <small>{r.tipo}</small>}{isCrossSell(r) && <span className="list-pill list-pill--cross">Cross-sell</span>}</div></div>
              <div role="cell" className="customer-order-item-value"><small>Qta</small>{r.quantita}</div>
              <div role="cell" className="customer-order-item-value customer-order-item-value--unit-price"><small>Prezzo</small>{formatCurrency(r.unit_price)}</div>
              <div role="cell" className="customer-order-item-value"><small>Totale</small><strong>{formatCurrency(r.line_total)}</strong></div>
            </div>)}
          </div>
        </section>
        <section className="customer-order-panel" aria-labelledby="customer-order-total-title">
          <h2 id="customer-order-total-title">Riepilogo economico</h2>
          <dl className="customer-order-info">
            <div><dt>Sconti/Promo</dt><dd>{order ? order.sconto_cents > 0 ? `-${formatCurrency(order.sconto_cents)}` : "-" : loading ? "…" : "-"}</dd></div>
            <div><dt>Spese Trasporto</dt><dd>{order ? order.logistics_fee ? formatCurrency(order.logistics_fee) : "-" : loading ? "…" : "-"}</dd></div>
            <div className="customer-order-total"><dt>Totale</dt><dd>{order ? formatCurrency(order.total_cents) : loading ? "…" : "-"}</dd></div>
          </dl>
        </section>
        <section className="customer-order-panel" aria-labelledby="customer-order-delivery-title">
          <h2 id="customer-order-delivery-title">Altre informazioni</h2>
          <dl className="customer-order-info">
            <div><dt><Truck size={20} aria-hidden="true" />Data consegna</dt><dd>{order?.data_consegna ? formatDate(order.data_consegna) : "-"}</dd></div>
            <div><dt><MapPin size={20} aria-hidden="true" />Indirizzo</dt><dd>{order?.pickup_address?.full_address || "-"}</dd></div>
          </dl>
        </section>
      </main>
    </div>
  );

  return (
    <div className="list-page list-page--detail">
      <header className="list-header list-header--with-back">
        <button
          type="button"
          className="btn btn-ghost btn-icon list-header-back"
          onClick={() => {
            if (backTo) navigate(backTo);
            else navigate(-1);
          }}
          aria-label="Indietro"
          title="Indietro"
        >
          ←
        </button>

        <h1>Ordine #{id}</h1>
      </header>

      {/* Riepilogo ordine (uso la stessa UI dei filtri come layout a griglia) */}
      <section className="list-filters">
        <div className="list-filterbar list-filterbar--summary">
          <div className="list-filter-field ods-field ods-field--supplier">
            <label className="list-filter-label">Fornitore</label>
            <div className="ods-value">
              {order?.fornitore ?? (loading ? "…" : "—")}
            </div>
          </div>

          <div className="list-filter-field ods-field ods-field--date">
            <label className="list-filter-label">Data ordine</label>
            <div className="ods-value">
              {order?.created_at ? formatDate(order.created_at) : loading ? "…" : "—"}
            </div>
          </div>

          <div className="list-filter-field ods-field ods-field--nr">
            <label className="list-filter-label">Nr ordine</label>
            <div className="ods-orderid">{id}</div>
          </div>

          <div className="list-filter-field ods-field ods-field--status">
            <label className="list-filter-label">Stato</label>
            <div className="ods-status-pill">
              {order?.status ?? (loading ? "…" : "—")}
            </div>
          </div>
        </div>
      </section>

      <main className="list-main">
        <AddToCalendarButtons order={order} actor="customer" disabled={loading} />

        {error && !loading && (
          <div className="list-empty" style={{ marginBottom: 10 }}>
            {error}
          </div>
        )}

        {/* Tabella articoli */}
        <div
          className="list-table list-table--details"
          role="table"
          aria-label="Dettaglio ordine"
        >
          <div className="list-row list-row--head" role="row">
            <div className="list-cell" role="columnheader">
              Articolo
            </div>
            <div
              className="list-cell list-ta-center"
              role="columnheader"
            >
              Qta
            </div>
            <div
              className="list-cell list-ta-right"
              role="columnheader"
            >
              Prezzo
            </div>
            <div
              className="list-cell list-ta-right"
              role="columnheader"
            >
              Totale
            </div>
          </div>

          {loading &&
            Array.from({ length: 4 }).map((_, i) => (
              <div
                className="list-row list-skel"
                key={`sk-${i}`}
                role="row"
                aria-busy="true"
              >
                <div className="list-cell">.</div>
                <div className="list-cell">.</div>
                <div className="list-cell list-ta-right">.</div>
                <div className="list-cell list-ta-right">.</div>
              </div>
            ))}

          {!loading && !hasRows && (
            <div className="list-empty">Nessun articolo.</div>
          )}

          {!loading &&
            hasRows &&
            items.map((r) => (
              <div className="list-row" role="row" key={r.id}>
                <div className="list-cell" role="cell" data-label="Articolo">
                  <span className="list-item-title">
                    {r.descrizione}
                    {r.tipo ? (
                      <small style={{ color: "var(--muted)" }}>
                        {" · "}
                        {r.tipo}
                      </small>
                    ) : null}
                  </span>
                  {isCrossSell(r) && (
                    <span className="list-pill list-pill--cross">
                      Cross-sell
                    </span>
                  )}
                </div>

                <div
                  className="list-cell list-ta-center"
                  role="cell"
                  data-label="Qta"
                >
                  {r.quantita}
                </div>
                <div
                  className="list-cell list-ta-right"
                  role="cell"
                  data-label="Prezzo"
                >
                  {formatCurrency(r.unit_price)}
                </div>
                <div
                  className="list-cell list-ta-right"
                  role="cell"
                  data-label="Totale"
                >
                  {formatCurrency(r.line_total)}
                </div>
              </div>
            ))}
        </div>

        {/* Riepilogo finale */}
        <div
          className="list-table list-table--summary list-table--compact"
          style={{ marginTop: 12 }}
          role="table"
          aria-label="Riepilogo"
        >
          <div className="list-row" role="row">
            <div
              className="list-cell"
              role="cell"
              data-label="Sconti/Promo"
            >
              Sconti/Promo
            </div>
            <div
              className="list-cell list-ta-right"
              role="cell"
              data-label="Valore"
            >
              {order
                ? order.sconto_cents > 0
                  ? `−${formatCurrency(order.sconto_cents)}`
                  : "—"
                : loading
                  ? "…"
                  : "—"}
            </div>
          </div>

          <div className="list-row" role="row">
            <div
              className="list-cell"
              role="cell"
              data-label="Spese Trasporto"
            >
              Spese Trasporto
            </div>
            <div
              className="list-cell list-ta-right"
              role="cell"
              data-label="Valore"
            >
              {order
                ? order.logistics_fee
                  ? formatCurrency(order.logistics_fee)
                  : "—"
                : loading
                  ? "…"
                  : "—"}
            </div>
          </div>

          <div className="list-row" role="row">
            <div
              className="list-cell"
              role="cell"
              data-label="Data consegna"
            >
              Data consegna
            </div>
            <div
              className="list-cell list-ta-right"
              role="cell"
              data-label="Valore"
            >
              {order?.data_consegna ? formatDate(order.data_consegna) : "—"}
            </div>
          </div>

          <div className="list-row" role="row">
            <div className="list-cell" role="cell" data-label="Indirizzo">
              Indirizzo
            </div>
            <div
              className="list-cell list-ta-right"
              role="cell"
              data-label="Valore"
            >
              {order?.pickup_address?.full_address || "—"}
            </div>
          </div>

          <div className="list-row" role="row">
            <div className="list-cell" role="cell" data-label="Totale">
              Totale
            </div>
            <div
              className="list-cell list-ta-right"
              role="cell"
              data-label="Valore"
            >
              {order
                ? formatCurrency(order.total_cents)
                : loading
                  ? "…"
                  : "—"}
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
